import type { WatchStopHandle } from "vue"

import { watchThrottled } from "@vueuse/core"

import { EntityMimeTypeId } from "../entity"
import { InterfaceFile } from "../file/InterfaceFile"
import { sendFileDownloadRequests } from "../handlers/file/download"
import { Gesture } from "./BaseGesture"

export interface ImageRendererGestureState {
  current: InterfaceFile
  isDragging: boolean
  isWheeling: boolean
  viewX: number
  viewY: number
  viewScaleExp: number
  loading?: boolean
  errored?: boolean
  watchers: WatchStopHandle[]
}

class ImageRendererGestures extends Gesture {
  rendererState: Ref<ImageRendererGestureState>
  container: Ref<HTMLDivElement | undefined>
  image: Ref<HTMLImageElement | undefined>

  #dragStartX = 0
  #dragStartY = 0
  #viewStartX = 0
  #viewStartY = 0
  #SCROLL_SCALE_FACTOR = 0.0025

  constructor(rendererState: Ref<ImageRendererGestureState>, gestureLayer: number, container: Ref<HTMLDivElement | undefined>, image: Ref<HTMLImageElement | undefined>) {
    super(gestureLayer)
    this.rendererState = rendererState
    this.container = container
    this.image = image
  }

  override reset(): void {
    this.rendererState.value.isDragging = false
    this.rendererState.value.isWheeling = false
    this.#dragStartX = 0
    this.#dragStartY = 0
    this.#viewStartX = 0
    this.#viewStartY = 0
  }

  override async onMounted() {
    this.rendererState.value.loading = true

    this.rendererState.value.watchers.push(watchImmediate(() => this.rendererState.value.current, (preview) => {
      if (isDefined(preview))
        this.displayImage()
    }))
    this.rendererState.value.watchers.push(watchThrottled(
      () => this.cssTransform(),
      () => this.image.value!.style.transform = this.cssTransform(),
      { throttle: 1000 / 30 },
    ))
  }

  override async onUnmounted() {
    this.rendererState.value.watchers.forEach(w => w())
  }

  override async onMouseDown(mouse: MouseEvent) {
    const { isPrimaryClick } = useGestures()
    if (!isPrimaryClick(mouse))
      return this.reset()
    if (!getFirstMatchingParent(
      mouse.target as HTMLDivElement,
      t => t === this.container.value,
    ))
      return this.reset()

    console.debug("Started preview drag")
    this.capture()
    this.rendererState.value.isDragging = true
    this.#dragStartX = mouse.pageX
    this.#dragStartY = mouse.pageY
    this.#viewStartX = this.rendererState.value.viewX
    this.#viewStartY = this.rendererState.value.viewY
  }

  override async onMouseMove(mouse: MouseEvent) {
    if (!this.rendererState.value.isDragging)
      return this.reset()

    this.capture()
    const distX = mouse.pageX - this.#dragStartX
    const distY = mouse.pageY - this.#dragStartY
    this.rendererState.value.viewX = this.#viewStartX + distX / Math.exp(this.rendererState.value.viewScaleExp)
    this.rendererState.value.viewY = this.#viewStartY + distY / Math.exp(this.rendererState.value.viewScaleExp)
  }

  override async onMouseUp(_mouse: MouseEvent) {
    this.reset()
  }

  override async onMouseEnter(_mouse: MouseEvent) {
    const { state: gestureState } = useGestures()
    if (!gestureState.value.mouseButtonsPressed.has(0) || !this.rendererState.value.isDragging)
      return this.reset()
  }

  override async onWheel(wheel: WheelEvent) {
    if (!getFirstMatchingParent(
      wheel.target as HTMLDivElement,
      t => t === this.container.value,
    ))
      return this.reset()
    const zoomY = wheel.deltaY * this.#SCROLL_SCALE_FACTOR
    this.rendererState.value.viewScaleExp -= zoomY // zoom inverse to scale
  }

  async onDownloadPrepared(response: FileDownloadResponseDatagram) {
    const { removeInterfaceEventListenerByRequestId } = useEvents()
    removeInterfaceEventListenerByRequestId("fileDownloadResponse", response.requestId)
    if (response.error || !response.file || !this.image.value)
      return console.error("cannot preview/peek image", response)

    // hooray, the image is already cached
    if (!response.presignedUri)
      return console.debug("Have presigned uri, file is already cached")

    this.rendererState.value.current.mergeWithNewer(new InterfaceFile(response.file))
    const mimeType = this.rendererState.value.current.mimeType()!

    if ([
      EntityMimeTypeId.ImageJpeg,
      EntityMimeTypeId.ImageWebp,
      EntityMimeTypeId.ImagePng,
      EntityMimeTypeId.ImageGif,
      EntityMimeTypeId.ImageSvg,
    ].includes(mimeType.value)) {
      this.image.value!.src = response.presignedUri
      this.image.value!.style.transform = this.cssTransform()
      this.rendererState.value.loading = false
      return
    }

    // TODO: download the asset and transcode as png
    this.rendererState.value.errored = true
    return this.console.warn("Cannot currently preview non-web-friendly images")
  }

  async displayImage() {
    const { loadCachedImageAsTranscodedPngUrl, sandboxPathAsWebFriendlyUri, getFileOfflineStatus } = useSandbox()
    const { addInterfaceEventListener } = useEvents()
    const currentFile = this.rendererState.value.current
    const bounds = this.container.value?.getBoundingClientRect()
    if (!currentFile || !currentFile.isImage() || !this.image.value || !bounds || !currentFile.mimeType())
      return console.warn("No file or not an image, likely loading", currentFile)

    const mimeType = currentFile.mimeType()!
    const sandboxPath = await getFileOfflineStatus(currentFile)

    if (!sandboxPath) {
      const requestId = generateWorkerRequestId()
      addInterfaceEventListener("fileDownloadResponse", res => this.onDownloadPrepared(res), { requestId })
      const fileId = currentFile.fileId
      const file = InterfaceFile.blank(fileId).inner()
      sendFileDownloadRequests([{
        requestId,
        file,
        offline: false,
      }])
      return
    }

    if ([
      EntityMimeTypeId.ImageJpeg,
      EntityMimeTypeId.ImageWebp,
      EntityMimeTypeId.ImagePng,
      EntityMimeTypeId.ImageGif,
      EntityMimeTypeId.ImageSvg,
    ].includes(mimeType.value)) {
      const url = await sandboxPathAsWebFriendlyUri(sandboxPath)
      this.image.value.src = url
      this.image.value!.style.transform = this.cssTransform()
      this.rendererState.value.loading = false
    }
    else {
      console.warn("file not a web displayable image", currentFile)
      const imageSrc = await loadCachedImageAsTranscodedPngUrl(currentFile)
      this.image.value.src = imageSrc
      this.image.value!.style.transform = this.cssTransform()
      this.rendererState.value.loading = false
      URL.revokeObjectURL(this.image.value.src)
    }
  }

  cssTransform() {
    return `scale(${Math.exp(this.rendererState.value.viewScaleExp)}) translate3d(${this.rendererState.value.viewX}px, ${this.rendererState.value.viewY}px, 0px)`
  }
}
export default function (
  container: Ref<HTMLDivElement | undefined>,
  image: Ref<HTMLImageElement | undefined>,
  gestureLayer: number,
  rendererState: Ref<ImageRendererGestureState>,
) {
  return new ImageRendererGestures(rendererState, gestureLayer, container, image)
}
